/**
 * Folder Store (Zustand)
 *
 * Centralized folder state management for document dashboard.
 * Manages folder tree structure and folder CRUD operations.
 *
 * @module features/dashboard/store/folderStore
 * @see SPEC-016 Requirement 2.1
 */

import { create } from 'zustand';
import type { ApiError } from '@linkode/shared';
import type {
  FolderInput,
  CreateFolderRequestInput,
  RenameFolderRequestInput,
} from '@linkode/shared';
import type { FolderNode } from '@linkode/shared';
import { createFolderId } from '@linkode/shared';
import { dashboardApiClient } from '../api/dashboardApiClient';

// ============================================================================
// Types
// ============================================================================

/**
 * Folder state interface
 *
 * @see Requirement 2.1
 */
export interface FolderState {
  // ─────────────────────────────────────────────────────────────────────────
  // Folder Data
  // ─────────────────────────────────────────────────────────────────────────

  /** Flat list of all folders */
  folders: FolderInput[];

  /** Hierarchical folder tree for sidebar display */
  folderTree: FolderNode[];

  /** Currently selected folder ID (for navigation) */
  selectedFolderId: string | null;

  /** Folder IDs that are expanded in the tree view */
  expandedFolderIds: Set<string>;

  // ─────────────────────────────────────────────────────────────────────────
  // Loading & Error State
  // ─────────────────────────────────────────────────────────────────────────

  /** Loading state for folder operations */
  isLoading: boolean;

  /** Error from last operation */
  error: ApiError | null;

  /** Loading state for specific folder operations (keyed by folderId) */
  operationLoading: Record<string, boolean>;

  // ─────────────────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Fetch all folders for the current user
   */
  fetchFolders: () => Promise<void>;

  /**
   * Create a new folder
   * @param request - Folder creation request
   * @see Requirement 2.1
   */
  createFolder: (request: CreateFolderRequestInput) => Promise<string>;

  /**
   * Rename an existing folder
   * @param folderId - ID of the folder to rename
   * @param request - Rename request with new name
   * @see Requirement 2.2
   */
  renameFolder: (folderId: string, request: RenameFolderRequestInput) => Promise<void>;

  /**
   * Delete a folder (moves contained documents to parent)
   * @param folderId - ID of the folder to delete
   * @see Requirement 2.3
   */
  deleteFolder: (folderId: string) => Promise<void>;

  /**
   * Select a folder for navigation
   * @param folderId - Folder ID or null for root
   */
  selectFolder: (folderId: string | null) => void;

  /**
   * Toggle folder expansion in tree view
   * @param folderId - Folder ID to toggle
   */
  toggleFolderExpansion: (folderId: string) => void;

  /**
   * Expand a folder in tree view
   * @param folderId - Folder ID to expand
   */
  expandFolder: (folderId: string) => void;

  /**
   * Collapse a folder in tree view
   * @param folderId - Folder ID to collapse
   */
  collapseFolder: (folderId: string) => void;

  /**
   * Expand all folders to a specific path
   * @param path - Folder path to expand to
   */
  expandToPath: (path: string) => void;

  /**
   * Set error state
   * @param error - ApiError or null
   */
  setError: (error: ApiError | null) => void;

  /**
   * Clear error state
   */
  clearError: () => void;

  /**
   * Get folder by ID
   * @param folderId - Folder ID
   * @returns Folder or undefined
   */
  getFolderById: (folderId: string) => FolderInput | undefined;

  /**
   * Get folder by path
   * @param path - Folder path
   * @returns Folder or undefined
   */
  getFolderByPath: (path: string) => FolderInput | undefined;

  /**
   * Get child folders of a parent path
   * @param parentPath - Parent folder path
   * @returns Array of child folders
   */
  getChildFolders: (parentPath: string) => FolderInput[];
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build folder tree from flat folder list
 *
 * @param folders - Flat list of folders
 * @param expandedIds - Set of expanded folder IDs
 * @returns Hierarchical folder tree
 */
function buildFolderTree(folders: FolderInput[], expandedIds: Set<string>): FolderNode[] {
  // Create a map for quick lookup
  const folderMap = new Map<string, FolderNode>();

  // Initialize all folders as nodes
  for (const folder of folders) {
    folderMap.set(folder.path, {
      folderId: createFolderId(folder.folderId),
      name: folder.name,
      path: folder.path,
      children: [],
      isExpanded: expandedIds.has(folder.folderId),
    });
  }

  // Build tree structure
  const rootNodes: FolderNode[] = [];

  for (const folder of folders) {
    const node = folderMap.get(folder.path);
    if (!node) continue;

    if (folder.parentPath === '' || folder.parentPath === '/') {
      // Root level folder
      rootNodes.push(node);
    } else {
      // Find parent and add as child
      const parentNode = folderMap.get(folder.parentPath);
      if (parentNode) {
        parentNode.children.push(node);
      } else {
        // Parent not found, treat as root
        rootNodes.push(node);
      }
    }
  }

  // Sort children alphabetically at each level
  const sortNodes = (nodes: FolderNode[]): FolderNode[] => {
    return nodes
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((node) => ({
        ...node,
        children: sortNodes(node.children),
      }));
  };

  return sortNodes(rootNodes);
}

/**
 * Get all ancestor paths for a given path
 *
 * @param path - Folder path
 * @returns Array of ancestor paths
 */
function getAncestorPaths(path: string): string[] {
  if (!path || path === '' || path === '/') return [];

  const segments = path.replace(/^\/|\/$/g, '').split('/');
  const ancestors: string[] = [];

  for (let i = 1; i < segments.length; i++) {
    ancestors.push('/' + segments.slice(0, i).join('/'));
  }

  return ancestors;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  folders: [] as FolderInput[],
  folderTree: [] as FolderNode[],
  selectedFolderId: null as string | null,
  expandedFolderIds: new Set<string>(),
  isLoading: false,
  error: null as ApiError | null,
  operationLoading: {} as Record<string, boolean>,
};

// ============================================================================
// Store Creation
// ============================================================================

/**
 * Folder Store
 *
 * Zustand store for folder state management.
 *
 * Features:
 * - Folder tree structure
 * - CRUD operations
 * - Tree expansion state
 * - Folder navigation
 *
 * @example
 * ```tsx
 * // Get folder tree
 * const folderTree = useFolderStore((state) => state.folderTree);
 *
 * // Create folder
 * const createFolder = useFolderStore((state) => state.createFolder);
 * await createFolder({ name: 'My Folder', parentPath: '' });
 *
 * // Toggle expansion
 * const toggleFolderExpansion = useFolderStore((state) => state.toggleFolderExpansion);
 * toggleFolderExpansion(folderId);
 * ```
 *
 * @see Requirement 2.1
 */
export const useFolderStore = create<FolderState>()((set, get) => ({
  ...initialState,

  // ─────────────────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────────────────

  fetchFolders: async () => {
    set({ isLoading: true, error: null });

    try {
      // Note: The API endpoint for listing folders is not yet implemented
      // This will be connected when the folders list endpoint is available
      // For now, we'll use an empty array as placeholder
      // TODO: Connect to actual API when GET /folders endpoint is available
      const folders: FolderInput[] = [];

      const { expandedFolderIds } = get();
      const folderTree = buildFolderTree(folders, expandedFolderIds);

      set({
        folders,
        folderTree,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error as ApiError,
        isLoading: false,
      });
    }
  },

  createFolder: async (request) => {
    set({ isLoading: true, error: null });

    try {
      const response = await dashboardApiClient.createFolder(request);

      // Create new folder object
      const newFolder: FolderInput = {
        folderId: createFolderId(response.folderId),
        name: request.name,
        path: response.path,
        parentPath: request.parentPath || '',
        ownerId: '', // Will be set by server
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      // Update state
      const { folders, expandedFolderIds } = get();
      const updatedFolders = [...folders, newFolder];
      const folderTree = buildFolderTree(updatedFolders, expandedFolderIds);

      set({
        folders: updatedFolders,
        folderTree,
        isLoading: false,
      });

      return response.folderId;
    } catch (error) {
      set({
        error: error as ApiError,
        isLoading: false,
      });
      throw error;
    }
  },

  renameFolder: async (folderId, request) => {
    set((state) => ({
      operationLoading: { ...state.operationLoading, [folderId]: true },
      error: null,
    }));

    try {
      const response = await dashboardApiClient.renameFolder(folderId, request);

      // Update folder in state
      const { folders, expandedFolderIds } = get();
      const updatedFolders = folders.map((folder) => {
        if (folder.folderId === folderId) {
          return {
            ...folder,
            name: request.name,
            path: response.newPath,
            updatedAt: Date.now(),
          };
        }
        // Update child folders' paths
        if (folder.path.startsWith(response.oldPath + '/')) {
          return {
            ...folder,
            path: folder.path.replace(response.oldPath, response.newPath),
            parentPath: folder.parentPath.replace(response.oldPath, response.newPath),
            updatedAt: Date.now(),
          };
        }
        return folder;
      });

      const folderTree = buildFolderTree(updatedFolders, expandedFolderIds);

      set((state) => ({
        folders: updatedFolders,
        folderTree,
        operationLoading: { ...state.operationLoading, [folderId]: false },
      }));
    } catch (error) {
      set((state) => ({
        error: error as ApiError,
        operationLoading: { ...state.operationLoading, [folderId]: false },
      }));
      throw error;
    }
  },

  deleteFolder: async (folderId) => {
    set((state) => ({
      operationLoading: { ...state.operationLoading, [folderId]: true },
      error: null,
    }));

    try {
      await dashboardApiClient.deleteFolder(folderId);

      // Remove folder from state
      const { folders, expandedFolderIds, selectedFolderId } = get();
      const deletedFolder = folders.find((f) => f.folderId === folderId);
      const updatedFolders = folders.filter((folder) => {
        // Remove the folder itself
        if (folder.folderId === folderId) return false;
        // Remove child folders
        if (deletedFolder && folder.path.startsWith(deletedFolder.path + '/')) return false;
        return true;
      });

      // Update expanded IDs
      const newExpandedIds = new Set(expandedFolderIds);
      newExpandedIds.delete(folderId);

      const folderTree = buildFolderTree(updatedFolders, newExpandedIds);

      // Clear selection if deleted folder was selected
      const newSelectedFolderId = selectedFolderId === folderId ? null : selectedFolderId;

      set((state) => ({
        folders: updatedFolders,
        folderTree,
        expandedFolderIds: newExpandedIds,
        selectedFolderId: newSelectedFolderId,
        operationLoading: { ...state.operationLoading, [folderId]: false },
      }));
    } catch (error) {
      set((state) => ({
        error: error as ApiError,
        operationLoading: { ...state.operationLoading, [folderId]: false },
      }));
      throw error;
    }
  },

  selectFolder: (folderId) => {
    set({ selectedFolderId: folderId });
  },

  toggleFolderExpansion: (folderId) => {
    const { expandedFolderIds, folders } = get();
    const newExpandedIds = new Set(expandedFolderIds);

    if (newExpandedIds.has(folderId)) {
      newExpandedIds.delete(folderId);
    } else {
      newExpandedIds.add(folderId);
    }

    const folderTree = buildFolderTree(folders, newExpandedIds);

    set({
      expandedFolderIds: newExpandedIds,
      folderTree,
    });
  },

  expandFolder: (folderId) => {
    const { expandedFolderIds, folders } = get();

    if (expandedFolderIds.has(folderId)) return;

    const newExpandedIds = new Set(expandedFolderIds);
    newExpandedIds.add(folderId);

    const folderTree = buildFolderTree(folders, newExpandedIds);

    set({
      expandedFolderIds: newExpandedIds,
      folderTree,
    });
  },

  collapseFolder: (folderId) => {
    const { expandedFolderIds, folders } = get();

    if (!expandedFolderIds.has(folderId)) return;

    const newExpandedIds = new Set(expandedFolderIds);
    newExpandedIds.delete(folderId);

    const folderTree = buildFolderTree(folders, newExpandedIds);

    set({
      expandedFolderIds: newExpandedIds,
      folderTree,
    });
  },

  expandToPath: (path) => {
    const { expandedFolderIds, folders } = get();
    const ancestorPaths = getAncestorPaths(path);

    // Find folder IDs for ancestor paths
    const ancestorFolderIds = folders
      .filter((f) => ancestorPaths.includes(f.path))
      .map((f) => f.folderId);

    const newExpandedIds = new Set([...expandedFolderIds, ...ancestorFolderIds]);
    const folderTree = buildFolderTree(folders, newExpandedIds);

    set({
      expandedFolderIds: newExpandedIds,
      folderTree,
    });
  },

  setError: (error) => {
    set({ error });
  },

  clearError: () => {
    set({ error: null });
  },

  getFolderById: (folderId) => {
    return get().folders.find((f) => f.folderId === folderId);
  },

  getFolderByPath: (path) => {
    return get().folders.find((f) => f.path === path);
  },

  getChildFolders: (parentPath) => {
    return get().folders.filter((f) => f.parentPath === parentPath);
  },
}));

// ============================================================================
// Selectors
// ============================================================================

/**
 * Select flat folder list
 */
export const selectFolders = (state: FolderState): FolderInput[] => state.folders;

/**
 * Select folder tree
 */
export const selectFolderTree = (state: FolderState): FolderNode[] => state.folderTree;

/**
 * Select selected folder ID
 */
export const selectSelectedFolderId = (state: FolderState): string | null => state.selectedFolderId;

/**
 * Select expanded folder IDs
 */
export const selectExpandedFolderIds = (state: FolderState): Set<string> => state.expandedFolderIds;

/**
 * Select loading state
 */
export const selectIsLoading = (state: FolderState): boolean => state.isLoading;

/**
 * Select error state
 */
export const selectError = (state: FolderState): ApiError | null => state.error;

/**
 * Select operation loading state for a specific folder
 */
export const selectOperationLoading =
  (folderId: string) =>
  (state: FolderState): boolean =>
    state.operationLoading[folderId] ?? false;
